/* eslint-disable no-unused-vars */
import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js';
import { Typography } from 'antd';

const { Text } = Typography;

// Fixed custom draw function that handles BOTH horizontal and vertical bars
Chart.elements.Rectangle.prototype.draw = function () {
    const { ctx } = this._chart;
    const vm = this._view;
    const cornerRadius = 6;
        
    let left;
    let right;
    let top; 
    let bottom;
    let borderSkipped;
    let signX;
    let signY;
    let { borderWidth } = vm.borderWidth;

    // Handle borderWidth as object or number
    if (typeof borderWidth !== 'object') {
        borderWidth = {
            top: borderWidth,
            right: borderWidth,
            bottom: borderWidth,
            left: borderWidth
        };
    }

    if (vm.horizontal) {
        // HORIZONTAL BAR
        left = vm.base;
        right = vm.x;
        top = vm.y - vm.height / 2;
        bottom = vm.y + vm.height / 2;
        signX = right > left ? 1 : -1;
        signY = 1;
        borderSkipped = vm.borderSkipped || 'left';
    } else {
        // VERTICAL BAR
        left = vm.x - vm.width / 2;
        right = vm.x + vm.width / 2;
        top = vm.y;
        bottom = vm.base;
        signX = 1;
        signY = bottom > top ? 1 : -1;
        borderSkipped = vm.borderSkipped || 'bottom';
    }

    // Skip drawing if the bar is not visible
    if (vm.hidden) return;

    ctx.beginPath();
    ctx.fillStyle = vm.backgroundColor;
    ctx.strokeStyle = vm.borderColor;
    ctx.lineWidth = borderWidth.top || borderWidth;

    // Calculate dimensions
    const width = Math.abs(right - left);
    const height = Math.abs(bottom - top);
    const x = Math.min(left, right);
    const y = Math.min(top, bottom);

    // Adjust radius if it's too large for the bar size
    let radius = cornerRadius;
    if (radius > height / 2) {
        radius = Math.floor(height / 2);
    }
    if (radius > width / 2) {
        radius = Math.floor(width / 2);
    }

    // Create rounded rectangle path
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);

    ctx.fill();
    
    if (borderWidth.top > 0) {
        ctx.stroke();
    }
};

const HorizontalBar = ({ data, color = '#8884d8', legendLabel = 'Frequency' }) => {
    const canvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) {
            return () => {}; // Return empty cleanup function
        }
    
        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                display: true,
                position: 'bottom',
            },
            plugins: {
                datalabels: {
                  anchor: 'center',
                  align: 'center',
                  color: 'white',
                  font: {
                    weight: 'regular',
                    size: 12
                  },
                  formatter: (value) => {
                    return value.toLocaleString();
                  }
                }
              },            
            scales: {
                xAxes: [{
                    display: true,
                    gridLines: { 
                        borderDash: [3, 3],
                        display: true
                    },
                    ticks: { 
                        beginAtZero: true,
                        callback(value) {
                            return value.toLocaleString();
                        }
                    },
                }],
                yAxes: [{
                    gridLines: { display: false },
                    ticks: {
                        fontSize: 12
                    }
                }],
            },
            tooltips: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    // eslint-disable-next-line no-shadow
                    label(tooltipItem, data) {
                        const label = data.datasets[tooltipItem.datasetIndex].label || '';
                        const value = tooltipItem.xLabel.toLocaleString();
                        return `${label}: ${value}`;
                    }
                }
            },
        };
    
        // Create chart instance
        chartInstanceRef.current = new Chart(canvasRef.current, {
            type: 'horizontalBar',
            data: {
                labels: [],
                datasets: [],
            },
            options: chartOptions,
        });
    
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, []);

    useEffect(() => {
        if (!chartInstanceRef.current || !data || data.length === 0) return;

        // Update chart data
        chartInstanceRef.current.data.labels = data.map(d => d.label);
        chartInstanceRef.current.data.datasets = [{
            label: legendLabel,
            data: data.map(d => d.value),
            backgroundColor: color,
            borderColor: color,
            borderWidth: 1,
        }];
        
        chartInstanceRef.current.update();
    }, [data, color, legendLabel]);

    if (!data || data.length === 0) {
        return <Text type="secondary">No data available</Text>;
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '300px' }}>
            <canvas ref={canvasRef} />
        </div>
    );   
};

export default HorizontalBar;